import React, { useState } from 'react';
import { motion } from 'framer-motion';
import networkService from '../../services/NetworkService';

const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="bg-neutral-800 p-5 rounded-xl border border-neutral-700 hover:border-primary-400/50 
      transition-colors group"
  >
    <div className="w-10 h-10 rounded-lg bg-primary-400/10 flex items-center justify-center mb-4 
      group-hover:bg-primary-400/20 transition-colors">
      <svg
        className="w-5 h-5 text-primary-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
      </svg>
    </div>
    <h3 className="text-lg font-medium text-neutral-200 mb-2">{title}</h3>
    <p className="text-sm text-neutral-400">{description}</p>
  </motion.div>
);

const ComingSoon = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    stage: 'ideation', // Default value
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const features = [
    {
      icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
      title: "AI-Powered Market Research",
      description: "Get instant access to market size, trends, and competitor analysis. Our AI helps you identify opportunities and validate your business idea with real data."
    },
    {
      icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
      title: "Smart Financial Planning",
      description: "Interactive cost modeling, profitability analysis, and pricing strategies backed by industry benchmarks. Plan your business with confidence."
    },
    {
      icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
      title: "Comprehensive Validation Suite",
      description: "From automated surveys to A/B testing, get real feedback from your target audience. Includes AI-powered sentiment analysis and market validation tools."
    }
  ];

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await networkService.joinWaitlist(formData);
      setIsSubmitted(true);
    } catch (error) {
      // Handle error (show toast notification, etc.)
      console.error('Failed to join waitlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="flex justify-center mb-8">
            <img
              src="/vettor_icon_transparent.png"
              alt="Vettor Logo"
              className="h-16 w-auto"
            />
          </div>

          <h1 className="text-5xl font-bold text-neutral-100 mb-6">
            Turn Your Ideas Into Reality
          </h1>
          <p className="text-xl text-neutral-400 max-w-3xl mx-auto mb-8">
            Vettor is your all-in-one platform for validating and launching successful businesses. 
            Get AI-powered market research, financial modeling, and validation tools to make 
            data-driven decisions at every step.
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} delay={index * 0.1} />
          ))}
        </div>

        {/* Subscribe Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-neutral-800 rounded-xl p-8 border border-neutral-700 shadow-xl max-w-md mx-auto"
        >
          {!isSubmitted ? (
            <>
              <h2 className="text-2xl font-semibold text-neutral-200 mb-2">
                Be the First to Know
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg 
                      text-neutral-200 placeholder-neutral-400 focus:outline-none focus:ring-2 
                      focus:ring-primary-400 focus:border-transparent transition-colors"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg 
                      text-neutral-200 placeholder-neutral-400 focus:outline-none focus:ring-2 
                      focus:ring-primary-400 focus:border-transparent transition-colors"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="stage" className="block text-sm font-medium text-neutral-300 mb-1">
                    Current Stage
                  </label>
                  <select
                    id="stage"
                    name="stage"
                    value={formData.stage}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg 
                      text-neutral-200 focus:outline-none focus:ring-2 
                      focus:ring-primary-400 focus:border-transparent transition-colors"
                  >
                    <option value="ideation">Just an Idea</option>
                    <option value="validation">Validating the Idea</option>
                    <option value="research">Doing Market Research</option>
                    <option value="planning">Planning & Strategy</option>
                    <option value="mvp">Building MVP</option>
                    <option value="testing">Testing with Users</option>
                    <option value="launched">Already Launched</option>
                    <option value="growing">Growing & Scaling</option>
                    <option value="pivoting">Pivoting Direction</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition-colors
                    ${isLoading 
                      ? 'bg-neutral-600 text-neutral-400 cursor-not-allowed'
                      : 'bg-primary-400 text-neutral-900 hover:bg-primary-500'
                    }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" 
                          stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" 
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Joining Waitlist...</span>
                    </div>
                  ) : (
                    'Join Waitlist'
                  )}
                </button>
              </form>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <svg
                className="w-12 h-12 text-primary-400 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-lg font-medium text-neutral-200 mb-1">
                You're on the list!
              </h3>
              <p className="text-sm text-neutral-400">
                We'll notify you as soon as we launch.
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Footer */}
        <div className="mt-16 text-center">
          <p className="text-sm text-neutral-500">
            © 2024 Vettor. All rights reserved. <a href="mailto:hello@thevettor.com" className="hover:text-primary-400 transition-colors">divkhare@thevettor.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;

